import { useEffect, useState } from "react";
import { app, HostName } from "@microsoft/teams-js";
import { setLanguageTranslation } from "./language";

export type ThemeString = "light" | "dark" | "contrast";

function normalizeTheme(theme?: string): ThemeString {
  switch (theme) {
    case "dark":
    case "contrast":
      return theme;
    default:
      return "light";
  }
}

function getSystemTheme(): ThemeString {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function useTeamsContext() {
  const [inTeams, setInTeams] = useState<boolean | null>(null); // null = not initialized yet
  const [themeString, setThemeString] = useState<ThemeString>("light");
  const [language, setLanguage] = useState<string>("en");

  useEffect(() => {
    let mediaQuery: MediaQueryList | null = null;

    const init = async () => {
      try {
        // Try initializing Teams SDK
        await app.initialize();
        const context = await app.getContext();

        const host = context.app.host?.name;

        const isTeamsHost =
          host === HostName.teams ||
          host === HostName.teamsModern;

        if (!isTeamsHost) {
          throw new Error("Not Teams host");
        }

        setInTeams(true);

        // ---------------- THEME ----------------
        const teamsTheme = context.app.theme;

        const normalizedTheme =
          teamsTheme === "default"
            ? "light"
            : normalizeTheme(teamsTheme);

        setThemeString(normalizedTheme);
        localStorage.setItem("Theme", normalizedTheme);

        app.registerOnThemeChangeHandler((newTheme) => {
          const updatedTheme =
            newTheme === "default"
              ? "light"
              : normalizeTheme(newTheme);

          setThemeString(updatedTheme);
          localStorage.setItem("Theme", updatedTheme);
        });

        // ---------------- LANGUAGE ----------------
        const locale = context.app.locale;
        const normalizedLocale = locale?.split("-")[0] ?? "en";
        setLanguage(normalizedLocale);

        const tenantUrl = localStorage.getItem("tenantUrl");
        if (tenantUrl) {
          await setLanguageTranslation(normalizedLocale, false);
        }
      } catch {
        // ---------------- STANDALONE BROWSER ----------------
        setInTeams(false);

        // Language
        const locale = navigator.language;
        const normalizedLocale = locale?.split("-")[0] ?? "en";
        setLanguage(normalizedLocale);

        const tenantUrl = localStorage.getItem("tenantUrl");
        if (tenantUrl) {
          await setLanguageTranslation(normalizedLocale, false);
        }

        // Theme
        mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

        const applyTheme = () => {
          const systemTheme = mediaQuery!.matches ? "dark" : "light";
          setThemeString(systemTheme);
          localStorage.setItem("Theme", systemTheme);
        };

        applyTheme();
        mediaQuery.addEventListener("change", applyTheme);
      }
    };

    init();

    return () => {
      if (mediaQuery) {
        mediaQuery.removeEventListener("change", () => {});
      }
    };
  }, []);

  return {
    inTeams, // null = loading, true = Teams, false = browser
    themeString,
    language,
  };
}